/**
 * Local Whisper STT via WASM (Transformers.js ONNX Runtime Web).
 *
 * Architecture note: this adapter runs OpenAI Whisper locally in the browser.
 * The public SpeechToTextService contract stays stable if we later swap the
 * runtime to ggml whisper.cpp WASM builds.
 *
 * The ONNX/WASM session lives in whisperPipelineCache for the page lifetime.
 * This class only owns microphone capture + callbacks, so Pingflo remounts
 * do not reload the model or leak extra WASM heaps.
 */

import { AudioCapture, decodeBlobToPcm16k } from "./AudioCapture";
import { checkBrowserCapability } from "./BrowserCapability";
import { DEFAULT_MODEL_ID, ModelManager, normalizeProgressEvent } from "./ModelManager";
import { loadWhisperPipeline, peekWhisperPipeline } from "./whisperPipelineCache";
import { normalizeTranscription } from "./TranscriptionNormalizer";
import { VoiceState } from "./voiceState";

const LANG_HINTS = {
  en: "english",
  fr: "french",
  de: "german",
  es: "spanish",
  nl: "dutch",
  pt: "portuguese",
  it: "italian",
};

const LANG_FROM_NAME = Object.fromEntries(
  Object.entries(LANG_HINTS).map(([code, name]) => [name, code])
);

/**
 * Normalize Whisper language output to ISO 639-1 used by AI Coach.
 * @param {string|null|undefined} lang
 * @returns {string|null}
 */
export function toCoachLanguage(lang) {
  if (!lang) return null;
  const raw = String(lang).toLowerCase().trim();
  if (["en", "fr", "de", "es", "nl", "pt"].includes(raw)) return raw;
  if (["en", "fr", "de", "es", "nl", "pt"].includes(raw.substring(0, 2)) && raw.length === 2) {
    return raw.substring(0, 2);
  }
  if (LANG_FROM_NAME[raw]) return LANG_FROM_NAME[raw];
  const two = raw.substring(0, 2);
  return ["en", "fr", "de", "es", "nl", "pt"].includes(two) ? two : null;
}

export class WhisperWasmService {
  /**
   * @param {{ modelId?: string }} [options]
   */
  constructor(options = {}) {
    this.modelId = options.modelId || DEFAULT_MODEL_ID;
    this._pipeline = peekWhisperPipeline(this.modelId);
    this._capture = null;
    this._state = VoiceState.IDLE;
    this._disposed = false;
    this._modelManager = new ModelManager(this.modelId);

    this._onPartial = null;
    this._onFinal = null;
    this._onError = null;
    this._onProgress = null;
    this._onState = null;
    this._onLevel = null;

    /** @type {object|null} last timing metrics */
    this.lastMetrics = this._pipeline
      ? { modelLoadMs: 0, modelId: this.modelId, cached: true }
      : null;
  }

  onPartial(cb) {
    this._onPartial = cb;
  }
  onFinal(cb) {
    this._onFinal = cb;
  }
  onError(cb) {
    this._onError = cb;
  }
  onProgress(cb) {
    this._onProgress = cb;
  }
  onState(cb) {
    this._onState = cb;
  }
  onLevel(cb) {
    this._onLevel = cb;
  }

  getState() {
    return this._state;
  }

  _setState(state) {
    if (this._disposed) return;
    this._state = state;
    if (this._onState) this._onState(state);
  }

  _emitError(code, message, cause) {
    this._setState(code);
    if (this._onError) this._onError({ code, message, cause });
  }

  _bindPipeline(pipeline, { modelLoadMs, cached }) {
    this._pipeline = pipeline;
    this.lastMetrics = {
      ...(this.lastMetrics || {}),
      modelLoadMs,
      modelId: this.modelId,
      cached,
    };
  }

  /**
   * Download + load Whisper model (shared after first success for this page).
   */
  async initialize() {
    if (this._disposed) return;
    const cap = checkBrowserCapability();
    if (!cap.ok) {
      this._emitError(cap.reason || VoiceState.UNSUPPORTED_BROWSER, "Browser not capable of local STT");
      throw new Error(cap.reason || VoiceState.UNSUPPORTED_BROWSER);
    }

    const ready = peekWhisperPipeline(this.modelId);
    if (ready) {
      this._bindPipeline(ready, { modelLoadMs: 0, cached: true });
      return;
    }

    this._setState(VoiceState.LOADING);
    const loadStarted = performance.now();

    try {
      const transcriber = await loadWhisperPipeline(this.modelId, {
        onProgress: (ev) => {
          if (this._disposed || !this._onProgress) return;
          this._onProgress(normalizeProgressEvent(ev));
        },
      });
      if (this._disposed) return;

      this._bindPipeline(transcriber, {
        modelLoadMs: Math.round(performance.now() - loadStarted),
        cached: await this._modelManager.isCached(),
      });
      await this._modelManager.markCached();
      this.lastMetrics = {
        ...(this.lastMetrics || {}),
        cached: await this._modelManager.isCached(),
      };
      this._setState(VoiceState.IDLE);
    } catch (err) {
      if (this._disposed) return;
      const msg = (err && err.message) || String(err);
      const oom = /out of memory|oom|memory/i.test(msg);
      const code = oom ? VoiceState.OUT_OF_MEMORY : VoiceState.MODEL_LOAD_ERROR;
      this._emitError(code, msg, err);
      throw err;
    }
  }

  /**
   * Start push-to-talk recording.
   * @param {{ languageHint?: string }} [opts]  ISO 639-1 hint (en/fr/de/es)
   */
  async startListening(opts = {}) {
    if (this._disposed) return;
    this._languageHint = opts.languageHint || null;

    try {
      if (!this._pipeline) {
        await this.initialize();
      }
      if (this._disposed || !this._pipeline) return;
    } catch {
      return;
    }

    try {
      if (this._capture) {
        this._capture.dispose();
        this._capture = null;
      }
      this._capture = new AudioCapture();
      const micStarted = performance.now();
      await this._capture.start({
        onLevel: (level) => {
          if (this._onLevel) this._onLevel(level);
        },
      });
      this.lastMetrics = {
        ...(this.lastMetrics || {}),
        micStartMs: Math.round(performance.now() - micStarted),
      };
      this._setState(VoiceState.LISTENING);
      if (this._onPartial) this._onPartial("");
    } catch (err) {
      if (this._capture) {
        this._capture.dispose();
        this._capture = null;
      }
      const name = err?.name || "";
      const msg = err?.message || "";
      const code =
        msg === "PERMISSION_DENIED" || name === "NotAllowedError"
          ? VoiceState.PERMISSION_DENIED
          : msg === "NO_MICROPHONE" ||
              name === "NotFoundError" ||
              name === "DevicesNotFoundError"
            ? VoiceState.NO_MICROPHONE
            : VoiceState.TRANSCRIPTION_ERROR;
      this._emitError(code, msg || "Failed to start microphone", err);
    }
  }

  /**
   * Stop recording, transcribe, emit final text (normalized). Never auto-sends to Coach.
   */
  async stopListening() {
    if (this._disposed) return;
    if (!this._capture) {
      this._setState(VoiceState.IDLE);
      return;
    }

    this._setState(VoiceState.PROCESSING);
    let blob;
    let durationMs = 0;
    try {
      const stopped = await this._capture.stop();
      blob = stopped.blob;
      durationMs = stopped.durationMs;
    } catch (err) {
      this._emitError(VoiceState.TRANSCRIPTION_ERROR, "Failed to stop recording", err);
      if (this._capture) {
        this._capture.dispose();
        this._capture = null;
      }
      return;
    }
    this._capture = null;

    if (!blob || blob.size < 256) {
      this._emitError(VoiceState.TRANSCRIPTION_ERROR, "Recording too short");
      return;
    }

    const transcribeStarted = performance.now();
    let pcm = null;
    try {
      const decoded = await decodeBlobToPcm16k(blob);
      pcm = decoded.pcm;
      const durationSec = decoded.durationSec;
      blob = null;

      const asrOptions = {
        task: "transcribe",
      };
      // Chunking allocates extra WASM tensors; only needed for long takes.
      if (durationSec > 30) {
        asrOptions.chunk_length_s = 30;
        asrOptions.stride_length_s = 5;
      }
      const hint = this._languageHint && LANG_HINTS[this._languageHint];
      if (hint) asrOptions.language = hint;

      const output = await this._pipeline(pcm, asrOptions);
      pcm = null;
      const raw = (output && output.text ? output.text : String(output || "")).trim();
      const text = normalizeTranscription(raw);
      const language =
        toCoachLanguage(output && output.language) ||
        toCoachLanguage(this._languageHint) ||
        null;

      const transcribeMs = Math.round(performance.now() - transcribeStarted);
      this.lastMetrics = {
        ...(this.lastMetrics || {}),
        recordDurationMs: Math.round(durationMs),
        audioDurationSec: durationSec,
        transcribeMs,
        peakHeapMb: peakHeapMb(),
      };

      this._setState(VoiceState.COMPLETED);
      if (this._onFinal) {
        this._onFinal({
          text,
          rawText: raw,
          language,
          metrics: this.lastMetrics,
        });
      }
      this._setState(VoiceState.IDLE);
    } catch (err) {
      pcm = null;
      blob = null;
      const msg = (err && err.message) || String(err);
      const oom = /out of memory|oom|memory/i.test(msg);
      this._emitError(
        oom ? VoiceState.OUT_OF_MEMORY : VoiceState.TRANSCRIPTION_ERROR,
        msg,
        err
      );
    }
  }

  /**
   * Release mic + callbacks. Does **not** unload the shared Whisper session.
   */
  dispose() {
    this._disposed = true;
    if (this._capture) {
      this._capture.dispose();
      this._capture = null;
    }
    this._onPartial = null;
    this._onFinal = null;
    this._onError = null;
    this._onProgress = null;
    this._onState = null;
    this._onLevel = null;
    this._pipeline = peekWhisperPipeline(this.modelId);
    this._state = VoiceState.IDLE;
  }
}

function peakHeapMb() {
  try {
    if (performance.memory && performance.memory.usedJSHeapSize) {
      return Math.round(performance.memory.usedJSHeapSize / (1024 * 1024));
    }
  } catch {
    /* Safari has no performance.memory */
  }
  return null;
}
